import React, { useState, useEffect } from 'react';
import axios from 'axios';

import {
  FaWallet, FaCrown, FaCreditCard, FaCheckCircle, FaClock,
  FaArrowRight, FaHistory, FaShieldAlt, FaGem,
  FaUniversity, FaMobile, FaGooglePay, FaSpinner,
  FaCheck, FaEye, FaLayerGroup
} from 'react-icons/fa';
import { message, Modal, Spin, Tag } from 'antd';

import { 
  getPremiumPlans, 
  createRazorpayOrder, 
  verifyRazorpayPayment,
  getUserWallet,
  buyPlanFromWallet
} from '../../Api/CoreApi';
import { BASE_API } from "../../Api/Api";

const Recharge = () => {
  const BASE_URL = BASE_API;

  const [allActivePlans, setAllActivePlans] = useState([]);
  const [primaryPlan, setPrimaryPlan] = useState({
    status: 'No Active Plan',
    plan_name: 'Free',
    days_left: 0,
    start_date: 'N/A',
    end_date: 'N/A'
  });

  const [premiumPlans, setPremiumPlans] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  
  // ✅ Wallet & Payment States
  const [walletBalance, setWalletBalance] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('online'); 
  
  const [fetching, setFetching] = useState(true);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
  });

  const getPlanConfig = (planType) => {
    const configs = {
      contact_view: { icon: '📱', color: '#3b82f6', label: 'Contact View' },
      profile_boost: { icon: '🚀', color: '#f59e0b', label: 'Profile Boost' },
      specific_user: { icon: '👤', color: '#8b5cf6', label: 'Special Plan' },
      custom: { icon: '⚙️', color: '#10b981', label: 'Custom' },
      premium: { icon: '💎', color: '#ff0055', label: 'Premium' },
    };
    return configs[planType] || configs.premium;
  };

  const getIconByType = (type) => getPlanConfig(type).icon;

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    setFetching(true);
    try {
      await Promise.all([
        fetchAllMemberships(),
        fetchPremiumPlans(),
        fetchRechargeHistory(),
        fetchWalletData()
      ]);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setFetching(false);
    }
  };

  const fetchWalletData = async () => {
    try {
      const wallet = await getUserWallet();
      setWalletBalance(Number(wallet?.balance || 0));
    } catch (err) {
      setWalletBalance(0);
    }
  };

  const fetchAllMemberships = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/membership/current/`, getAuthHeaders());
      const data = response.data;
      let plansArray = [];

      if (data.plans && Array.isArray(data.plans) && data.plans.length > 0) {
        plansArray = data.plans.map(plan => ({
          ...plan,
          contact_view_limit: plan.contact_view_limit || 0,
          boost_position: plan.boost_position || '',
          is_extended: plan.is_extended || false,
        }));
      } else if (data.plan_name && data.plan_name !== 'Free') {
        plansArray = [{
          id: data.id || 1, plan_id: data.plan_id, plan_name: data.plan_name,
          plan_type: data.plan_type || 'custom', status: data.status,
          start_date: data.start_date, expiry_date: data.expiry_date,
          days_left: data.days_left || 0, amount_paid: data.plan_price || 0,
          payment_mode: 'N/A', source: 'self_purchase', contact_view_limit: 0,
          boost_position: '', is_extended: false,
        }];
      }

      setAllActivePlans(plansArray);
      if (plansArray.length > 0) {
        const primary = plansArray[0];
        setPrimaryPlan({
          status: 'active', plan_name: primary.plan_name,
          days_left: primary.days_left, start_date: primary.start_date,
          end_date: primary.expiry_date,
        });
      } else {
        setPrimaryPlan({ status: 'No Active Plan', plan_name: 'Free', days_left: 0, start_date: 'N/A', end_date: 'N/A' });
      }
    } catch (err) {
      setAllActivePlans([]);
    }
  };

  const fetchPremiumPlans = async () => {
    try {
      const token = localStorage.getItem('access_token');
      let data = [];
      if (token) {
        const res = await axios.get(`${BASE_URL}/premium-plans/`, getAuthHeaders());
        data = res.data || [];
      } else {
        data = await getPremiumPlans();
      }
      const formatted = (data || []).map(plan => {
        const config = getPlanConfig(plan.plan_type || 'premium');
        return {
          id: plan.id, name: plan.plan_name, plan_type: plan.plan_type || 'custom',
          duration: `${plan.duration} Days`, price: `₹${Number(plan.price).toLocaleString('en-IN')}`,
          priceRaw: Number(plan.price), durationRaw: plan.duration,
          features: plan.features ? plan.features.split(',').map(f => f.trim()).filter(Boolean) : ['Premium Access'],
          popular: plan.is_popular || false, status: plan.status,
          contact_view_limit: plan.contact_view_limit || 0, boost_position: plan.boost_position || '',
          icon: config.icon, typeColor: config.color, typeLabel: config.label,
        };
      });
      setPremiumPlans(formatted);
    } catch (error) {
      setPremiumPlans([]);
    }
  };

  const fetchRechargeHistory = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/recharge/history/`, getAuthHeaders());
      setHistoryData(res.data || []);
    } catch (err) {
      setHistoryData([]);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan.id);
    const alreadyHas = allActivePlans.find(p => p.plan_id === plan.id || p.plan_name === plan.name);
    
    if (walletBalance >= plan.priceRaw) setPaymentMethod('wallet');
    else setPaymentMethod('online');

    if (alreadyHas) {
      message.info(`ℹ️ You already have "${plan.name}". Buying again will EXTEND your existing plan.`);
    } else {
      message.success(`✅ ${plan.name} selected`);
    }
    
    setTimeout(() => {
      document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  // ✅ WALLET PAYMENT LOGIC
  const handleWalletPayment = async (plan) => {
    if (walletBalance < plan.priceRaw) {
      message.error(`Insufficient balance! Need ₹${plan.priceRaw}, Available ₹${walletBalance}`);
      return;
    }
    setPaymentProcessing(true);
    try {
      const result = await buyPlanFromWallet(plan.id);
      if (result.success) {
        message.success(`🎉 ${plan.name} activated successfully from Wallet!`);
        await fetchAllData();
        setSelectedPlan(null);
      } else {
        message.error(result.error || "Purchase failed");
      }
    } catch (err) {
      message.error(err?.response?.data?.error || err?.message || "Payment failed");
    } finally {
      setPaymentProcessing(false);
    }
  };

  // ✅ ONLINE PAYMENT LOGIC
  const handleOnlinePayment = async (plan) => {
    if (typeof window.Razorpay === 'undefined') {
      message.error("Razorpay SDK not loaded. Please refresh.");
      return;
    }
    setPaymentProcessing(true);
    try {
      message.loading('Creating order...', 0);
      const userId = localStorage.getItem("user_id");

      const orderData = { planId: plan.id, paymentMethod: 'razorpay', user_id: userId, amount: plan.priceRaw };
      const response = await createRazorpayOrder(orderData);
      message.destroy();

      if (!response.success) {
        message.error(response.error || "Failed to create order");
        setPaymentProcessing(false);
        return;
      }

      const options = {
        key: response.razorpay_key || 'rzp_test_TQlquNaFwkZKZE',
        amount: response.amount_paise || (plan.priceRaw * 100),
        currency: "INR", name: "Rishtey Matrimony", description: `Plan: ${plan.name}`,
        image: 'https://communityrishtey.in/logo192.png', order_id: response.razorpay_order_id,
        handler: async function (paymentResponse) {
          try {
            message.loading('Verifying payment...', 0);
            const verifyData = {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
            };
            const verifyResult = await verifyRazorpayPayment(verifyData);
            message.destroy();

            if (verifyResult.success) {
              if (verifyResult.is_extension) message.success("🎉 Plan Extended Successfully!");
              else message.success("🎉 Payment Successful! Plan activated!");
              await fetchAllData();
              setSelectedPlan(null);
            } else {
              message.error(verifyResult.error || "Payment verification failed");
            }
          } catch (error) {
            message.destroy(); message.error("Payment verification failed. Please contact support.");
          }
          setPaymentProcessing(false);
        },
        modal: { ondismiss: function () { message.info("Payment cancelled"); setPaymentProcessing(false); }, escape: false, backdropclose: false },
        prefill: { name: localStorage.getItem("username") || "", email: localStorage.getItem("email") || "", contact: localStorage.getItem("contact") || "" },
        theme: { color: "#ff0055" },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      razorpay.on('payment.failed', function (response) {
        message.destroy(); message.error(`Payment failed: ${response.error.description}`);
        setPaymentProcessing(false);
      });
    } catch (error) {
      message.destroy(); message.error(error.response?.data?.error || "Payment failed. Try again.");
      setPaymentProcessing(false);
    }
  };

  const handleProceedPayment = () => {
    if (!selectedPlan) { message.warning("Please select a plan first"); return; }
    const plan = premiumPlans.find(p => p.id === selectedPlan);
    if (!plan) return;

    if (paymentMethod === 'wallet') handleWalletPayment(plan);
    else handleOnlinePayment(plan);
  };

  const handleUpgradePlan = () => {
    document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (fetching) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 16, padding: '20px' }}>
        <Spin size="large" />
        <p style={{ color: '#888', fontSize: 13 }}>Loading plans...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
        @keyframes rcShimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }

        .rc-page { background: #fff; min-height: 100vh; padding: 16px 12px; }
        .rc-wrap { max-width: 1280px; margin: 0 auto; }

        .rc-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; flex-wrap: wrap; gap: 10px; }
        .rc-title { font-size: 22px; font-weight: 800; color: #1a0a10; margin: 0; display: flex; align-items: center; gap: 9px; }
        .rc-title svg { color: #ff0055; }
        .rc-subtitle { color: #888; font-size: 12.5px; margin: 4px 0 0; }

        /* ⭐ ACTIVE PLANS SUMMARY */
        .rc-plans-summary {
          background: linear-gradient(135deg, #fff, #fffafc); border: 2px solid #ffe4ec;
          border-radius: 18px; padding: 20px; margin-bottom: 20px;
          box-shadow: 0 8px 24px rgba(255,0,85,.05); position: relative; overflow: hidden;
        }
        .rc-plans-summary::before {
          content: ""; position: absolute; top: 0; left: 0; width: 100%; height: 5px;
          background: linear-gradient(90deg, #ff0055, #ff4d88, #ff80aa, #ff0055);
          background-size: 200% 100%; animation: rcShimmer 3s linear infinite;
        }
        .rc-plans-summary-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 10px; }
        .rc-plans-summary-title { font-size: 16px; font-weight: 800; color: #1a0a10; margin: 0; display: flex; align-items: center; gap: 7px; }
        .rc-total-plans-badge { padding: 5px 12px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-radius: 999px; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; gap: 5px; }

        .rc-active-plans-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; margin-bottom: 16px; }
        .rc-active-plan-card { background: linear-gradient(135deg, #fff, #fffafc); border: 2px solid #ffe4ec; border-radius: 14px; padding: 14px; transition: all .3s; position: relative; }
        .rc-active-plan-card:hover { border-color: #ff80aa; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(255,0,85,.1); }
        .rc-active-plan-card.primary { background: linear-gradient(135deg, #fff0f3, #ffe4ec); border-color: #ff4d88; }
        .rc-primary-badge { position: absolute; top: -8px; right: 10px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 9px; padding: 3px 10px; border-radius: 999px; font-weight: 700; letter-spacing: 0.3px; }
        
        .rc-active-plan-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .rc-active-plan-icon { font-size: 28px; }
        .rc-active-plan-info h4 { font-size: 15px; font-weight: 800; color: #1a0a10; margin: 0 0 3px; }
        .rc-active-plan-info p { font-size: 11px; color: #888; margin: 0; }
        .rc-active-plan-status { padding: 3px 10px; border-radius: 999px; font-size: 10px; font-weight: 700; display: inline-flex; align-items: center; gap: 3px; }
        .rc-active-plan-status.active { background: linear-gradient(135deg, #4caf50, #66bb6a); color: #fff; }
        .rc-active-plan-status.expiring { background: linear-gradient(135deg, #ff9800, #ffc107); color: #fff; }

        .rc-active-plan-body { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 8px; }
        .rc-active-plan-detail { padding: 6px 10px; background: #fff; border-radius: 8px; border: 1px solid #ffe4ec; }
        .rc-active-plan-detail-label { font-size: 9px; color: #c2185b; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
        .rc-active-plan-detail-value { font-size: 12px; font-weight: 800; color: #1a0a10; margin-top: 2px; }
        .rc-active-plan-detail-value.highlight { color: #ff0055; }
        .rc-active-plan-detail-value.warning { color: #ff9800; }

        .rc-no-active-plan { text-align: center; padding: 30px 20px; background: linear-gradient(135deg, #fff5f8, #fff); border: 2px dashed #ffd3df; border-radius: 14px; }
        .rc-no-active-plan-icon { font-size: 48px; margin-bottom: 10px; color: #ffd3df; }
        .rc-no-active-plan h3 { font-size: 16px; color: #666; margin: 0 0 5px; }
        .rc-no-active-plan p { font-size: 12px; color: #999; margin: 0 0 14px; }

        .rc-upgrade-btn { padding: 11px 22px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border: none; border-radius: 999px; font-size: 13px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; box-shadow: 0 7px 18px rgba(255,0,85,.22); transition: all .3s; }
        .rc-upgrade-btn:hover { transform: translateY(-2px); }

        /* ⭐ PLANS GRID */
        .rc-section-title { font-size: 18px; font-weight: 800; color: #1a0a10; margin: 0 0 16px; display: flex; align-items: center; gap: 8px; }
        .rc-plans-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
        .rc-plan-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 18px; padding: 22px 18px; transition: all .35s; position: relative; box-shadow: 0 5px 18px rgba(255,0,85,.04); }
        .rc-plan-card:hover { transform: translateY(-5px); border-color: #ff80aa; box-shadow: 0 14px 32px rgba(255,0,85,.1); }
        .rc-plan-card.popular { border-color: #ff0055; box-shadow: 0 10px 28px rgba(255,0,85,.14); }
        .rc-plan-card.selected { border-color: #ff0055; background: linear-gradient(180deg, #fff, #fff5f7); box-shadow: 0 10px 28px rgba(255,0,85,.16); }
        .rc-plan-card.owned::after { content: "✓ ACTIVE"; position: absolute; top: 10px; right: 10px; background: linear-gradient(135deg, #4caf50, #66bb6a); color: #fff; font-size: 9px; padding: 3px 8px; border-radius: 999px; font-weight: 700; }
        .rc-popular-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 10.5px; padding: 4px 16px; border-radius: 999px; font-weight: 800; box-shadow: 0 5px 14px rgba(255,0,85,.28); white-space: nowrap; }
        
        .rc-plan-type-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 999px; font-size: 10px; font-weight: 700; margin-bottom: 8px; }
        .rc-plan-icon { font-size: 32px; text-align: center; margin-bottom: 5px; }
        .rc-plan-name { font-size: 17px; font-weight: 800; color: #1a0a10; margin: 3px 0; text-align: center; }
        .rc-plan-duration { font-size: 12px; color: #888; text-align: center; margin: 0; font-weight: 500; }
        .rc-plan-price { font-size: 32px; font-weight: 900; background: linear-gradient(135deg, #ff0055, #ff4d88); -webkit-background-clip: text; -webkit-text-fill-color: transparent; text-align: center; margin: 12px 0; }
        
        .rc-plan-features { list-style: none; padding: 0; margin: 10px 0 16px; }
        .rc-plan-feature { display: flex; align-items: flex-start; gap: 7px; padding: 5px 0; font-size: 12px; color: #555; line-height: 1.5; }
        
        .rc-plan-actions { display: flex; gap: 7px; }
        .rc-plan-btn { flex: 1; padding: 10px; border: 1.5px solid #ffd3df; border-radius: 12px; font-size: 12.5px; font-weight: 700; cursor: pointer; transition: all .3s; background: linear-gradient(135deg, #fff0f3, #ffe4ec); color: #ff0055; }
        .rc-plan-btn:hover { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055; transform: translateY(-2px); }
        .rc-plan-btn.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055; }
        .rc-plan-btn.extend { background: linear-gradient(135deg, #4caf50, #66bb6a); color: #fff; border-color: #4caf50; }
        .rc-view-btn { padding: 10px 13px; border: 1.5px solid #e8e8e8; border-radius: 12px; background: #f5f5f7; color: #888; cursor: pointer; transition: all .3s; flex-shrink: 0; }
        .rc-view-btn:hover { background: #e8e8e8; color: #ff0055; }

        /* ⭐ PAYMENT SECTION */
        .rc-payment-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 18px; padding: 24px; margin-bottom: 20px; box-shadow: 0 5px 18px rgba(255,0,85,.05); }
        .payment-methods-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
        .pay-method-box { border: 2px solid #e5e7eb; border-radius: 16px; padding: 16px; display: flex; align-items: center; gap: 14px; cursor: pointer; transition: all 0.3s; background: #fff; }
        .pay-method-box:hover:not(.disabled) { border-color: #ff80aa; background: #fffafc; }
        .pay-method-box.active { border-color: #ff0055; background: linear-gradient(135deg, #fff0f3, #ffe4ec); }
        .pay-method-box.disabled { opacity: 0.6; cursor: not-allowed; background: #f9fafb; }
        .pay-icon-circle { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
        .pay-method-box.active .pay-icon-circle { background: linear-gradient(135deg, #ff0055, #ff4d88) !important; color: #fff !important; }
        .pay-method-info { flex: 1; }
        .pay-method-title { font-size: 15px; font-weight: 800; color: #1a0a10; margin: 0 0 2px; }
        .pay-method-desc { font-size: 12px; color: #666; margin: 0; font-weight: 500;}
        .rc-pay-btn { width: 100%; padding: 16px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border: none; border-radius: 12px; font-size: 16px; font-weight: 800; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 9px; box-shadow: 0 8px 22px rgba(255,0,85,.24); transition: all .3s; }
        .rc-pay-btn:hover { transform: translateY(-2px); }
        .rc-pay-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none !important; }

        /* ⭐ HISTORY */
        .rc-history-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 18px; overflow: hidden; box-shadow: 0 5px 18px rgba(255,0,85,.05); }
        .rc-history-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 18px; border-bottom: 2px solid #ffe4ec; background: linear-gradient(135deg, #fffafc, #fff5f7); }
        .rc-history-table { width: 100%; border-collapse: collapse; }
        .rc-history-table th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700; color: #c2185b; text-transform: uppercase; }
        .rc-history-table td { padding: 12px 16px; font-size: 13px; border-bottom: 1px solid #fff0f3; }
        .rc-history-status { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; }

        @media (max-width: 1024px) {
          .rc-plans-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
          .rc-active-plans-grid { grid-template-columns: 1fr; }
        }

        @media (max-width: 768px) {
          .rc-page { padding: 12px 10px; }
          .rc-plans-grid { grid-template-columns: 1fr; gap: 14px; }
          .payment-methods-grid { grid-template-columns: 1fr; }
          .rc-payment-card { padding: 16px; }
        }
      `}</style>

      <div className="rc-page">
        <div className="rc-wrap">

          {/* HEADER */}
          <div className="rc-header">
            <div>
              <h1 className="rc-title"><FaWallet size={20} /> Recharge / Membership</h1>
              <p className="rc-subtitle">Upgrade your membership and unlock premium features 💖</p>
            </div>
          </div>

          {/* ⭐ ACTIVE PLANS SUMMARY */}
          <div className="rc-plans-summary">
            <div className="rc-plans-summary-header">
              <h3 className="rc-plans-summary-title"><FaCrown size={16} /> Your Active Plans</h3>
              {allActivePlans.length > 0 && (
                <span className="rc-total-plans-badge">
                  <FaLayerGroup size={11} /> {allActivePlans.length} Active
                </span>
              )}
            </div>

            {allActivePlans.length > 0 ? (
              <>
                <div className="rc-active-plans-grid">
                  {allActivePlans.map((plan, idx) => (
                    <div key={plan.id} className={`rc-active-plan-card ${idx === 0 ? 'primary' : ''}`}>
                      {idx === 0 && <span className="rc-primary-badge">⭐ PRIMARY</span>}
                      
                      <div className="rc-active-plan-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="rc-active-plan-icon">{getIconByType(plan.plan_type)}</span>
                          <div className="rc-active-plan-info">
                            <h4>{plan.plan_name}</h4>
                            <p>₹{plan.amount_paid || 0} • {plan.payment_mode || 'N/A'}</p>
                          </div>
                        </div>
                        <span className={`rc-active-plan-status ${plan.days_left <= 7 ? 'expiring' : 'active'}`}>
                          <FaCheckCircle size={9} /> {plan.days_left <= 7 ? 'Expiring Soon' : 'Active'}
                        </span>
                      </div>

                      <div className="rc-active-plan-body">
                        <div className="rc-active-plan-detail">
                          <div className="rc-active-plan-detail-label">Start</div>
                          <div className="rc-active-plan-detail-value">{plan.start_date || 'N/A'}</div>
                        </div>
                        <div className="rc-active-plan-detail">
                          <div className="rc-active-plan-detail-label">Expiry</div>
                          <div className="rc-active-plan-detail-value">{plan.expiry_date || 'N/A'}</div>
                        </div>
                        <div className="rc-active-plan-detail">
                          <div className="rc-active-plan-detail-label">Days Left</div>
                          <div className={`rc-active-plan-detail-value ${plan.days_left <= 7 ? 'warning' : 'highlight'}`}>{plan.days_left} days</div>
                        </div>
                        <div className="rc-active-plan-detail">
                          <div className="rc-active-plan-detail-label">Source</div>
                          <div className="rc-active-plan-detail-value">
                            {plan.source === 'admin_grant' ? '👑 Admin' : plan.source === 'staff_grant' ? '👥 Staff' : '💳 Self'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="rc-upgrade-btn" onClick={handleUpgradePlan}>
                  <FaCrown size={13} /> Buy More Plans
                </button>
              </>
            ) : (
              <div className="rc-no-active-plan">
                <div className="rc-no-active-plan-icon"><FaCrown /></div>
                <h3>No Active Plans</h3>
                <p>You don't have any active premium plans</p>
                <button className="rc-upgrade-btn" onClick={handleUpgradePlan}>
                  <FaGem size={13} /> Browse Plans
                </button>
              </div>
            )}
          </div>

          {/* ⭐ CHOOSE PLAN */}
          <h3 className="rc-section-title" id="plans-section"><FaGem size={16} /> Choose a Plan</h3>

          <div className="rc-plans-grid">
            {premiumPlans.length > 0 ? (
              premiumPlans.map(plan => {
                const isOwned = allActivePlans.some(p => p.plan_id === plan.id || p.plan_name === plan.name);
                return (
                  <div key={plan.id} className={`rc-plan-card ${plan.popular ? 'popular' : ''} ${selectedPlan === plan.id ? 'selected' : ''} ${isOwned ? 'owned' : ''}`}>
                    {plan.popular && <span className="rc-popular-badge">⭐ Most Popular</span>}
                    <div style={{ textAlign: 'center', marginTop: plan.popular ? 12 : 0 }}>
                      <span className="rc-plan-type-badge" style={{ background: `${plan.typeColor}15`, color: plan.typeColor, border: `1.5px solid ${plan.typeColor}40` }}>
                        {plan.icon} {plan.typeLabel}
                      </span>
                    </div>
                    <div className="rc-plan-icon" style={{ marginTop: 7 }}>{plan.icon}</div>
                    <h4 className="rc-plan-name">{plan.name}</h4>
                    <p className="rc-plan-duration">{plan.duration}</p>
                    <div className="rc-plan-price">{plan.price}</div>

                    <ul className="rc-plan-features">
                      {plan.features.map((f, idx) => (
                        <li key={idx} className="rc-plan-feature"><FaCheckCircle size={11} color="#4caf50" /> {f}</li>
                      ))}
                    </ul>

                    <div className="rc-plan-actions">
                      <button className={`rc-plan-btn ${selectedPlan === plan.id ? 'active' : ''} ${isOwned && selectedPlan !== plan.id ? 'extend' : ''}`} onClick={() => handleSelectPlan(plan)} disabled={paymentProcessing}>
                        {selectedPlan === plan.id ? '✅ Selected' : isOwned ? '🔄 Extend Plan' : 'Choose Plan'}
                      </button>
                      <button className="rc-view-btn" onClick={() => setSelectedPlanDetails(plan)} title="View Details">
                        <FaEye size={13} />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '36px 16px', color: '#999' }}>No plans available</div>
            )}
          </div>

          {/* ⭐ PAYMENT SECTION (NEW WALLET + ONLINE UI) */}
          <div className="rc-payment-card" id="payment-section">
            <h4 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaShieldAlt color="#4caf50" /> Select Payment Method
            </h4>

            {selectedPlan ? (() => {
              const plan = premiumPlans.find(p => p.id === selectedPlan);
              const canPayWithWallet = walletBalance >= plan.priceRaw;
              const isExtending = allActivePlans.some(p => p.plan_id === plan?.id || p.plan_name === plan?.name);

              return (
                <>
                  <div style={{ padding: '12px 16px', background: '#fff0f3', borderRadius: 12, border: '1px solid #ffd3df', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#ff0055' }}>
                        {plan.name} {isExtending && <Tag color="success" style={{marginLeft: 8}}>🔄 EXTENDING</Tag>}
                      </h4>
                      <p style={{ margin: 0, fontSize: 12, color: '#888' }}>Total Payable Amount</p>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#ff0055' }}>₹{plan.priceRaw}</div>
                  </div>

                  <div className="payment-methods-grid">
                    {/* ONLINE OPTION */}
                    <div className={`pay-method-box ${paymentMethod === 'online' ? 'active' : ''}`} onClick={() => setPaymentMethod('online')}>
                      <div className="pay-icon-circle" style={{ background: '#eff6ff', color: '#3b82f6' }}><FaCreditCard /></div>
                      <div className="pay-method-info">
                        <h4 className="pay-method-title">Pay Online</h4>
                        <p className="pay-method-desc">Cards, UPI, Netbanking</p>
                      </div>
                      {paymentMethod === 'online' && <FaCheckCircle color="#ff0055" size={18} />}
                    </div>

                    {/* WALLET OPTION */}
                    <div className={`pay-method-box ${paymentMethod === 'wallet' ? 'active' : ''} ${!canPayWithWallet ? 'disabled' : ''}`} onClick={() => { if(canPayWithWallet) setPaymentMethod('wallet'); }}>
                      <div className="pay-icon-circle" style={{ background: '#f0fdf4', color: '#16a34a' }}><FaWallet /></div>
                      <div className="pay-method-info">
                        <h4 className="pay-method-title">Promotional Wallet</h4>
                        <p className="pay-method-desc">Balance: <strong style={{color: '#16a34a'}}>₹{walletBalance}</strong></p>
                      </div>
                      {!canPayWithWallet ? <Tag color="error">Low Balance</Tag> : paymentMethod === 'wallet' && <FaCheckCircle color="#ff0055" size={18} />}
                    </div>
                  </div>

                  <button className="rc-pay-btn" onClick={handleProceedPayment} disabled={paymentProcessing}>
                    {paymentProcessing ? <><FaSpinner className="fa-spin" /> Processing...</> : `Proceed to Pay ₹${plan.priceRaw}`}
                  </button>
                </>
              );
            })() : (
              <div style={{ textAlign: 'center', padding: '30px 10px', background: '#f9fafb', borderRadius: 12 }}>
                <FaGem size={32} color="#cbd5e1" style={{ marginBottom: 10 }} />
                <p style={{ margin: 0, color: '#64748b', fontWeight: 600 }}>Please select a plan from above to continue payment</p>
              </div>
            )}
          </div>

          {/* ⭐ HISTORY */}
          <div className="rc-history-card">
            <div className="rc-history-header">
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}><FaHistory color="#ff0055" /> Recharge History</h4>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="rc-history-table">
                <thead><tr><th>Date</th><th>Plan</th><th>Amount</th><th>Status</th></tr></thead>
                <tbody>
                  {historyData.slice(0, 5).map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.created_at || item.date || 'N/A'}</td>
                      <td>{item.plan_name}</td>
                      <td style={{ fontWeight: 700, color: '#ff0055' }}>₹{item.amount}</td>
                      <td>
                        <span className="rc-history-status" style={{ background: item.status?.toLowerCase() === 'success' ? '#e8f5e9' : '#fff3e0', color: item.status?.toLowerCase() === 'success' ? '#2e7d32' : '#e65100'}}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* ⭐ PLAN DETAILS MODAL */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FaGem style={{ color: '#ff0055', fontSize: 18 }} /><span style={{ fontSize: 16, fontWeight: 700 }}>Plan Details</span></div>}
        open={!!selectedPlanDetails}
        onCancel={() => setSelectedPlanDetails(null)}
        footer={[
          <button key="select" style={{ marginRight: 8, padding: '9px 22px', border: 'none', borderRadius: 999, background: 'linear-gradient(135deg, #ff0055, #ff4d88)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }} onClick={() => { if (selectedPlanDetails) { handleSelectPlan(selectedPlanDetails); setSelectedPlanDetails(null); } }}>
            Choose This Plan
          </button>,
          <button key="close" style={{ padding: '9px 22px', border: '1.5px solid #ffe4ec', borderRadius: 999, background: '#fff', color: '#888', cursor: 'pointer', fontWeight: 700, fontSize: 13 }} onClick={() => setSelectedPlanDetails(null)}>
            Close
          </button>
        ]}
        width="90%" style={{ maxWidth: 480 }}
      >
        {selectedPlanDetails && (
          <div>
            <div style={{ textAlign: 'center', padding: '6px 0 14px' }}>
              <div style={{ fontSize: 42 }}>{selectedPlanDetails.icon}</div>
              <h3 style={{ fontSize: 21, fontWeight: 800, margin: '7px 0 3px' }}>{selectedPlanDetails.name}</h3>
              <p style={{ color: '#888', margin: 0, fontSize: 13 }}>{selectedPlanDetails.duration}</p>
              <p style={{ fontSize: 28, fontWeight: 900, color: '#ff0055', margin: '8px 0', lineHeight: 1.2 }}>{selectedPlanDetails.price}</p>
            </div>
            <div style={{ borderTop: '2px solid #ffe4ec', paddingTop: 14 }}>
              <h4 style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>✨ Features:</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {selectedPlanDetails.features.map((f, idx) => (
                  <li key={idx} style={{ padding: '6px 0', display: 'flex', alignItems: 'center', gap: 7, color: '#555', fontSize: 13, borderBottom: idx < selectedPlanDetails.features.length - 1 ? '1px solid #fff0f3' : 'none' }}>
                    <FaCheckCircle style={{ color: '#4caf50', fontSize: 12, flexShrink: 0 }} /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Recharge;